import { useEffect, useRef, useState } from 'react';
import { beginSupportTab, endSupportTab, readSupportSession, saveSupportSession, supportRequest } from '../lib/supportSession';

export default function SupportWorkspace() {
  const [context, setContext] = useState(null);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(true);
  const [ended, setEnded] = useState(false);
  const started = useRef(false);
  const generation = useRef(0);

  useEffect(() => {
    if (!context?.support_access?.expires_at) return;
    const remaining = new Date(context.support_access.expires_at).getTime() - Date.now();
    const timer = setTimeout(() => {
      generation.current += 1;
      endSupportTab(); setEnded(true); setContext(null); setRows([]); setSelected(null); setBusy(false);
      setError('Support access expired. Open a new session from Support.');
    }, Math.max(0, remaining));
    return () => clearTimeout(timer);
  }, [context]);

  useEffect(() => {
    // StrictMode must not redeem a single-use handoff twice.
    if (started.current) return;
    started.current = true;
    const code = new URLSearchParams(window.location.hash.slice(1)).get('code');
    window.history.replaceState(null, '', '/support/login');
    if (code) beginSupportTab();
    const version = generation.current;
    (async () => {
      if (code) saveSupportSession(await supportRequest('/exchange', { code }));
      if (!readSupportSession()) throw new Error('Open ActionWeave from the Support user page to begin a new session.');
      const auth = await supportRequest('/context');
      const executions = await supportRequest('/executions');
      if (generation.current === version) { setContext(auth); setRows(executions); }
    })().catch((e) => {
      if (generation.current === version) setError(e.message);
    }).finally(() => { if (generation.current === version) setBusy(false); });
  }, []);

  const load = async (nextOffset) => {
    setBusy(true); setError(''); setSelected(null);
    const version = generation.current;
    try {
      const executions = await supportRequest(`/executions?offset=${nextOffset}`);
      if (version === generation.current) { setRows(executions); setOffset(nextOffset); }
    } catch (e) {
      if (version === generation.current) { setError(e.message); setRows([]); }
    } finally { if (version === generation.current) setBusy(false); }
  };
  const inspect = async (id) => {
    setBusy(true); setError(''); setSelected(null);
    const version = generation.current;
    try {
      const result = await supportRequest(`/executions/${encodeURIComponent(id)}`);
      if (version === generation.current) setSelected(result);
    } catch (e) { if (version === generation.current) setError(e.message); }
    finally { if (version === generation.current) setBusy(false); }
  };
  const close = async () => {
    generation.current += 1;
    setBusy(true); setRows([]); setSelected(null); setContext(null); setError('');
    try { await supportRequest('/close', { close: true }); }
    catch { setError('The tab has ended. Server-side closure could not be confirmed; revoke this delegation in Support. It also expires automatically.'); }
    finally { endSupportTab(); setEnded(true); setBusy(false); }
  };
  const access = context?.support_access;
  return (
    <main className="min-h-screen bg-background text-foreground p-6 space-y-6">
      <header className="border border-border rounded-lg p-4 space-y-2">
        <h1 className="text-xl font-semibold">ActionWeave · Support workspace</h1>
        <p>Read-only execution history. Retry, approvals, order creation and settings changes are disabled.</p>
        {access && <>
          <p>Operator: {access.staff_name} · Customer: {access.target_name} · Organization: {access.tenant_name}</p>
          <p>Reason: {access.reason} · Expires: {new Date(access.expires_at).toLocaleString()}</p>
        </>}
        {!ended && <button className="border border-border rounded px-3 py-2" disabled={busy} onClick={close}>End support session</button>}
      </header>
      {error && <p role="alert">{error}</p>}
      {ended ? <p>Support session ended in this tab. You may close it.</p> : <>
        {busy && <p role="status">Loading…</p>}
        {context && <>
          <div className="flex gap-3">
            <button disabled={busy || offset === 0} onClick={() => load(Math.max(0, offset - 40))}>Previous</button>
            <button disabled={busy} onClick={() => load(offset)}>Refresh</button>
            <button disabled={busy || rows.length < 40} onClick={() => load(offset + 40)}>Next</button>
          </div>
          {!busy && rows.length === 0 && <p>No executions on this page.</p>}
          <div className="overflow-x-auto">
            <table className="w-full text-left"><thead><tr><th>Execution</th><th>Skill</th><th>Status</th><th>Created</th></tr></thead>
              <tbody>{rows.map((row) => <tr key={row.id} className="border-t border-border">
                <td><button className="underline p-2" disabled={busy} onClick={() => inspect(row.id)}>{row.id}</button></td>
                <td>{row.skill_id || '—'}</td><td>{row.status}</td><td>{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</td>
              </tr>)}</tbody>
            </table>
          </div>
          {selected && <section className="border border-border rounded-lg p-4 space-y-2" aria-label="Execution details">
            <h2 className="font-semibold">{selected.id}</h2>
            <p>Status: {selected.status} · Step: {selected.current_step ?? '—'}</p>
            <p className="whitespace-pre-wrap">{typeof selected.final_outcome === 'string' ? selected.final_outcome : JSON.stringify(selected.final_outcome ?? 'No final outcome recorded.')}</p>
            <p className="text-muted-foreground">Raw source bodies, connector payloads and credentials are not exposed in this support view.</p>
          </section>}
        </>}
      </>}
    </main>
  );
}
